import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { architectureData } from '@/mocks/homepageData';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

export default function ArchitectureSection() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#999]' : 'text-[#666]';
  const textMuted = isDark ? 'text-[#666]' : 'text-[#888]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  return (
    <section
      id="architecture"
      ref={sectionRef}
      className={`relative w-full py-16 md:py-24 border-t ${borderLight}`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 md:mb-14"
        >
          <span className="text-xs text-[#00ff88] font-mono uppercase tracking-widest">{t('arch.systemDesign')}</span>
          <h2 className={`mt-3 text-2xl md:text-3xl lg:text-4xl font-bold ${textPrimary}`}>
            {t('arch.dualEngine')}
          </h2>
          <p className={`mt-3 text-sm md:text-base max-w-2xl mx-auto ${textSecondary}`}>
            {t('arch.description')}
          </p>
        </motion.div>

        {/* Architecture Visual */}
        <div className="relative max-w-4xl mx-auto">
          {/* Connection lines - desktop only */}
          <div className="hidden lg:block absolute inset-0 pointer-events-none">
            <svg className="w-full h-full" viewBox="0 0 800 400" preserveAspectRatio="none">
              <defs>
                <linearGradient id="lineGrad1" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#00ff88" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#00ccff" stopOpacity="0.3" />
                </linearGradient>
                <linearGradient id="lineGrad2" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#ffaa00" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#ff5566" stopOpacity="0.3" />
                </linearGradient>
              </defs>
              {/* Horizontal lines */}
              <motion.line
                x1="200" y1="100" x2="600" y2="100"
                stroke="url(#lineGrad1)"
                strokeWidth="1"
                strokeDasharray="4 4"
                initial={{ pathLength: 0 }}
                animate={isInView ? { pathLength: 1 } : {}}
                transition={{ duration: 1, delay: 0.5 }}
              />
              <motion.line
                x1="200" y1="300" x2="600" y2="300"
                stroke="url(#lineGrad2)"
                strokeWidth="1"
                strokeDasharray="4 4"
                initial={{ pathLength: 0 }}
                animate={isInView ? { pathLength: 1 } : {}}
                transition={{ duration: 1, delay: 0.7 }}
              />
              {/* Vertical cross lines */}
              <motion.line
                x1="400" y1="100" x2="400" y2="300"
                stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}
                strokeWidth="1"
                strokeDasharray="4 4"
                initial={{ pathLength: 0 }}
                animate={isInView ? { pathLength: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.9 }}
              />
              {/* Center hub */}
              <motion.circle
                cx="400" cy="200" r="30"
                fill="rgba(0,255,136,0.05)"
                stroke="rgba(0,255,136,0.3)"
                strokeWidth="1"
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ duration: 0.5, delay: 1.1 }}
              />
              <motion.text
                x="400" y="205"
                textAnchor="middle"
                fill="#00ff88"
                fontSize="10"
                fontFamily="JetBrains Mono"
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.3, delay: 1.3 }}
              >
                ZhiSu
              </motion.text>
            </svg>
          </div>

          {/* Engine Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6 relative z-10">
            {architectureData.map((engine, index) => (
              <motion.div
                key={engine.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="glass-card-hover p-5 md:p-6 group"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${engine.color}15`,
                      border: `1px solid ${engine.color}30`,
                    }}
                  >
                    <i
                      className={`${engine.icon} w-6 h-6 flex items-center justify-center`}
                      style={{ color: engine.color }}
                    />
                  </div>
                  <div>
                    <h3 className={`text-base md:text-lg font-semibold group-hover:text-[#00ff88] transition-colors ${textPrimary}`}>
                      {t(`content.engines.${engine.id}.name`, engine.name)}
                    </h3>
                    <p className={`mt-1.5 text-sm leading-relaxed ${textSecondary}`}>
                      {t(`content.engines.${engine.id}.description`, engine.description)}
                    </p>

                    {/* Metrics */}
                    <div className="mt-4 flex items-center gap-4">
                      {index === 0 && (
                        <>
                          <span className={`text-xs font-mono ${textMuted}`}>
                            {t('arch.latency')}: <span className={textPrimary}>12ms</span>
                          </span>
                          <span className={`text-xs font-mono ${textMuted}`}>
                            {t('arch.models')}: <span className={textPrimary}>47</span>
                          </span>
                        </>
                      )}
                      {index === 1 && (
                        <>
                          <span className={`text-xs font-mono ${textMuted}`}>
                            {t('arch.depth')}: <span className={textPrimary}>$890M</span>
                          </span>
                          <span className={`text-xs font-mono ${textMuted}`}>
                            {t('arch.pairs')}: <span className={textPrimary}>200+</span>
                          </span>
                        </>
                      )}
                      {index === 2 && (
                        <>
                          <span className={`text-xs font-mono ${textMuted}`}>
                            {t('arch.tps')}: <span className={textPrimary}>50K</span>
                          </span>
                          <span className={`text-xs font-mono ${textMuted}`}>
                            {t('arch.uptime')}: <span className={textPrimary}>99.99%</span>
                          </span>
                        </>
                      )}
                      {index === 3 && (
                        <>
                          <span className={`text-xs font-mono ${textMuted}`}>
                            {t('arch.coverage')}: <span className={textPrimary}>100%</span>
                          </span>
                          <span className={`text-xs font-mono ${textMuted}`}>
                            {t('arch.alerts')}: <span className={textPrimary}>{t('hero.realtime')}</span>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}